import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { theme } from '../../theme.js';
import { useAuth } from '../../context/AuthContext.js';
import { useUnitReport, useEmailReport } from '../../lib/queries.js';
import { PRESETS, presetRange, periodLabel, reportCsv, type PresetKey } from '../../lib/report-period.js';
import { downloadText } from '../../lib/export.js';
import { emitSuccess } from '../../lib/toastBus.js';
import { UnitReportView } from '../../components/UnitReportView.js';
import { getThemePref } from '../../lib/themePref.js';

/**
 * Admin → Reports: pick a period and see how the unit went — on screen, as
 * a PDF (the print page), as a CSV, or emailed to yourself. ?from=&to=
 * opens a given period (the monthly email links here).
 */
export function ReportsPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const [defFrom, defTo] = presetRange('last-month');
  const from = params.get('from') ?? defFrom;
  const to = params.get('to') ?? defTo;
  const preset = PRESETS.find((p) => presetRange(p.key).join() === [from, to].join())?.key ?? 'custom';
  const { data, isLoading, error } = useUnitReport(orgId, from, to);
  const email = useEmailReport(orgId);

  const set = (f: string, t: string) => setParams({ from: f, to: t }, { replace: true });

  return (
    <div style={{ padding: '16px 16px 40px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={preset} aria-label="Period" onChange={(e) => {
          if (e.target.value !== 'custom') { const [f, t] = presetRange(e.target.value as PresetKey); set(f, t); }
        }} style={field}>
          {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          <option value="custom">Custom dates</option>
        </select>
        <input type="date" value={from} max={to} aria-label="From" onChange={(e) => e.target.value && set(e.target.value, to)} style={field} />
        <span style={{ color: theme.textMuted, fontSize: 13 }}>to</span>
        <input type="date" value={to} min={from} aria-label="To" onChange={(e) => e.target.value && set(from, e.target.value)} style={field} />
        <div style={{ flex: 1 }} />
        <button disabled={!data} onClick={() => nav(`/reports/print?from=${from}&to=${to}&print=1`)} style={btn}>PDF</button>
        <button disabled={!data} onClick={() => data && downloadText(`${data.orgName} report ${from} to ${to}.csv`, reportCsv(data))} style={btn}>CSV</button>
        <button disabled={!data || email.isPending} onClick={() => email.mutate({ from, to }, { onSuccess: (r) => emitSuccess(`Sent to ${r.sentTo}`) })}
          style={{ ...btn, background: theme.accent, color: theme.accentInk, border: 'none' }}>
          {email.isPending ? 'Sending…' : 'Email it to me'}
        </button>
      </div>
      <div style={{ fontSize: 12, color: theme.textMuted }}>
        {periodLabel(from, to)}. Admins also get last month’s report by email on the 1st (turn it off in Profile).
      </div>
      {isLoading && <div style={{ fontSize: 13, color: theme.textMuted }}>Adding it up…</div>}
      {error && <div style={{ fontSize: 13, color: theme.status.danger.fg }}>{(error as Error).message}</div>}
      {data && <UnitReportView r={data} />}
    </div>
  );
}

/** Printable report (Save as PDF from the print dialog). Opened with ?print=1 it prints by itself. */
export function UnitReportPrintPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const from = params.get('from') ?? '', to = params.get('to') ?? '';
  const { data } = useUnitReport(user?.orgId ?? '', from, to);

  // Paper is white: show it light whatever the app's appearance.
  useEffect(() => {
    document.documentElement.dataset.theme = 'light';
    return () => {
      const pref = getThemePref();
      if (pref === 'system') delete document.documentElement.dataset.theme;
      else document.documentElement.dataset.theme = pref;
    };
  }, []);
  useEffect(() => {
    if (data && params.get('print') === '1') setTimeout(() => window.print(), 400);
  }, [data, params]);

  return (
    <div className="report" style={{ background: '#fff', minHeight: '100%' }}>
      <div className="no-print" style={{ position: 'sticky', top: 0, zIndex: 5, display: 'flex', gap: 8, alignItems: 'center', padding: '10px 16px', background: '#f7f6f4', borderBottom: '1px solid #e3e1dc' }}>
        <button onClick={() => nav(`/admin/reports?from=${from}&to=${to}`)} style={btn}>← Back</button>
        <div style={{ flex: 1, fontSize: 12, color: '#6b6e78' }}>Choose “Save as PDF” as the printer to get a PDF.</div>
        <button onClick={() => window.print()} style={{ ...btn, background: '#4d5bd1', color: '#fff', border: 'none' }}>Save as PDF / Print</button>
      </div>
      <main style={{ maxWidth: 860, margin: '0 auto', padding: '24px 24px 40px' }}>
        {data ? <UnitReportView r={data} /> : <div style={{ color: '#6b6e78', fontFamily: theme.font }}>Preparing report…</div>}
      </main>
    </div>
  );
}

const field: React.CSSProperties = {
  padding: '8px 10px', fontSize: 14, borderRadius: 8, border: `1px solid ${theme.border}`,
  background: theme.surface, color: theme.text, fontFamily: theme.font,
};
const btn: React.CSSProperties = {
  padding: '8px 14px', borderRadius: 8, border: `1px solid ${theme.border}`, background: theme.surface2,
  color: theme.text, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
};
